import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import type { TripSummary } from '../../lib/types';
import {
  STYLE_LABELS,
  countryFlag,
  daysLabel,
  formatDateRange,
  nightsLabel,
  placesLabel,
} from '../../lib/format';
import { Badge, cx } from '../ui';
import { GENERATION_TIME_RANGE, isTripGenerating } from '../../lib/ai';

/**
 * Trip card.
 *
 * Used on the landing showcase, the gallery and the dashboard. When a trip has
 * no cover photo yet — which is every trip that has not been travelled — it
 * falls back to a deterministic gradient derived from the trip id, so the
 * gallery never looks like a wall of grey placeholders.
 */
export function TripCard({
  trip,
  to,
  showOwner = true,
  className,
}: {
  trip: TripSummary;
  to?: string;
  showOwner?: boolean;
  className?: string;
}) {
  const reduced = useReducedMotion();
  // A trip shared with the caller opens like their own: the private document,
  // editable when they are an editor.
  const isCollaborator = !trip.isOwner && (trip.myRole === 'editor' || trip.myRole === 'viewer');
  const href =
    to ??
    (trip.isOwner || isCollaborator
      ? `/app/trips/${trip.id}`
      : `/t/${trip.owner?.handle ?? '_'}/${trip.slug}`);
  const dateLabel = formatDateRange(trip.startDate, trip.endDate);
  const generating = isTripGenerating(trip);

  return (
    <motion.article
      whileHover={reduced || generating ? undefined : { y: -6 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={cx('surface-card group h-full overflow-hidden p-0', className)}
    >
      <Link to={href} className="flex h-full flex-col">
        <div className="relative aspect-[16/10] overflow-hidden">
          {trip.coverUrl ? (
            <img
              src={trip.coverUrl}
              alt=""
              loading="lazy"
              className="size-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
            />
          ) : (
            <div className={cx('size-full bg-gradient-to-br', gradientFor(trip.id))}>
              <div className="grid size-full place-items-center text-5xl opacity-40">
                {trip.countries[0] ? countryFlag(trip.countries[0]) : '🧭'}
              </div>
            </div>
          )}

          {generating ? (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-ink-900/55 px-6 text-center text-white backdrop-blur-[2px]">
              <span
                className={cx(
                  'size-10 rounded-full border-2 border-white/30 border-t-white',
                  reduced ? '' : 'animate-spin',
                )}
              />
              <div>
                <div className="text-sm font-semibold tracking-tight">Собираем маршрут</div>
                <p className="mt-1 text-xs leading-relaxed text-white/80">
                  Обычно {GENERATION_TIME_RANGE} — можно закрыть страницу, мы пришлём уведомление.
                </p>
              </div>
            </div>
          ) : null}

          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 to-transparent" />

          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            {trip.isFeatured ? (
              <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-ink-900 backdrop-blur">
                ★ Витрина
              </span>
            ) : null}
            {trip.isOwner ? (
              <span className="rounded-full bg-ink-900/80 px-2.5 py-1 text-xs font-medium text-sand-100 backdrop-blur">
                {visibilityLabel(trip.visibility)}
              </span>
            ) : null}
            {isCollaborator ? (
              <span className="rounded-full bg-lagoon-600/90 px-2.5 py-1 text-xs font-medium text-white backdrop-blur">
                👥 Совместное{trip.owner ? ` · @${trip.owner.handle}` : ''}
              </span>
            ) : null}
            {trip.completedAt ? (
              <span className="rounded-full bg-lagoon-600/90 px-2.5 py-1 text-xs font-medium text-white backdrop-blur">
                ✓ Пройдено
              </span>
            ) : null}
          </div>

          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3 text-white">
            <div className="flex items-center gap-1.5 text-sm">
              {trip.countries.slice(0, 3).map((code) => (
                <span key={code} title={code}>
                  {countryFlag(code)}
                </span>
              ))}
              {trip.nights ? <span className="opacity-90">{nightsLabel(trip.nights)}</span> : null}
            </div>
            {trip.forkCount > 0 ? (
              <span className="rounded-full bg-black/40 px-2 py-0.5 text-xs backdrop-blur">
                ⑂ {trip.forkCount}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <h3 className="text-lg font-semibold leading-snug tracking-tight">{trip.title}</h3>
          {trip.subtitle ? (
            <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-[var(--text-muted)]">
              {trip.subtitle}
            </p>
          ) : null}

          {trip.styles.length ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {trip.styles.slice(0, 3).map((style) => (
                <Badge key={style} tone="muted">
                  {STYLE_LABELS[style] ?? style}
                </Badge>
              ))}
            </div>
          ) : null}

          <div className="mt-auto flex items-center justify-between gap-3 pt-5 text-xs text-[var(--text-muted)]">
            <span className="truncate">
              {dateLabel ||
                [trip.dayCount ? daysLabel(trip.dayCount) : null, trip.placeCount ? placesLabel(trip.placeCount) : null]
                  .filter(Boolean)
                  .join(' · ')}
            </span>
            {showOwner && trip.owner ? (
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="grid size-5 place-items-center rounded-full bg-[var(--surface-sunken)] text-[0.7rem]">
                  {trip.owner.avatarEmoji ?? '🙂'}
                </span>
                {trip.owner.displayName ?? trip.owner.handle}
              </span>
            ) : null}
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

const GRADIENTS = [
  'from-clay-400 to-clay-600',
  'from-lagoon-400 to-lagoon-600',
  'from-sun-400 to-clay-500',
  'from-ink-600 to-ink-900',
  'from-lagoon-300 to-clay-500',
  'from-sun-300 to-lagoon-500',
];

/** Stable per-trip so a card does not change colour between renders. */
export function gradientFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return GRADIENTS[hash % GRADIENTS.length] as string;
}

function visibilityLabel(visibility: string): string {
  if (visibility === 'public') return 'Публичное';
  if (visibility === 'link') return 'По ссылке';
  return 'Приватное';
}

export function TripCardSkeleton() {
  return (
    <div className="surface-card overflow-hidden p-0">
      <div className="aspect-[16/10] animate-pulse bg-[var(--surface-sunken)]" />
      <div className="space-y-3 p-5">
        <div className="h-5 w-3/4 animate-pulse rounded bg-[var(--surface-sunken)]" />
        <div className="h-4 w-full animate-pulse rounded bg-[var(--surface-sunken)]" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-[var(--surface-sunken)]" />
      </div>
    </div>
  );
}
